import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useState } from "react"
import type { CodelistCondition, CodelistItem, CodelistValue } from "@/generated/sdk"
import { ReadOnlyValue } from "./ReadOnlyValue"

interface CodelistEditorProps {
  item: CodelistItem
  index: number
  onChange: (index: number, updated: CodelistItem) => void
  onRemove: (index: number) => void
  canEdit: boolean
  showErrors: boolean
  columnNames: string[]
  dqdlPreview: string
  showConditions: boolean
  showColumn?: boolean
  usedByColumns?: string[]
  duplicateName?: boolean
}

const MAX_VALUE_LEN = 255

export const CodelistEditor: React.FC<CodelistEditorProps> = ({
  item,
  index,
  onChange,
  onRemove,
  canEdit,
  showErrors,
  columnNames,
  dqdlPreview,
  showConditions,
  showColumn = true,
  usedByColumns = [],
  duplicateName = false,
}) => {
  const [sortCol, setSortCol] = useState<keyof CodelistValue | null>(null)
  const [sortAsc, setSortAsc] = useState(true)

  const sortedValues = sortCol
    ? [...item.values].sort((a, b) => {
        const av = (a[sortCol] || "").toLowerCase()
        const bv = (b[sortCol] || "").toLowerCase()
        return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av)
      })
    : item.values

  const handleSort = (col: keyof CodelistValue) => {
    if (sortCol === col) setSortAsc(!sortAsc)
    else {
      setSortCol(col)
      setSortAsc(true)
    }
  }

  const updateField = (field: keyof CodelistItem, value: unknown) => {
    onChange(index, { ...item, [field]: value })
  }

  const updateValue = (vi: number, field: keyof CodelistValue, value: string) => {
    const updated = [...item.values]
    updated[vi] = { ...updated[vi], [field]: value.slice(0, MAX_VALUE_LEN) }
    onChange(index, { ...item, values: updated })
  }

  const addValue = () => {
    onChange(index, {
      ...item,
      values: [...item.values, { code: "", decode: "", other_value: "" }],
    })
  }

  const removeValue = (vi: number) => {
    onChange(index, {
      ...item,
      values: item.values.filter((_, i) => i !== vi),
    })
  }

  const addCondition = () => {
    onChange(index, {
      ...item,
      conditions: [...(item.conditions || []), { column: "", value: "" }],
    })
  }

  const updateCondition = (ci: number, field: keyof CodelistCondition, value: string) => {
    const updated = [...(item.conditions || [])]
    updated[ci] = { ...updated[ci], [field]: value }
    onChange(index, { ...item, conditions: updated })
  }

  const removeCondition = (ci: number) => {
    onChange(index, {
      ...item,
      conditions: (item.conditions || []).filter((_, i) => i !== ci),
    })
  }

  const hasErrors =
    !item.name.trim() ||
    duplicateName ||
    (showColumn && !item.column.trim()) ||
    !item.values.some((v) => v.code.trim())

  const headerLabel = showColumn
    ? `${item.name || "Unnamed"} → ${item.column || "no column"} | ${item.values.length} values`
    : `${item.name || "Unnamed"} | ${item.values.length} values${usedByColumns.length ? ` | used by: ${usedByColumns.join(", ")}` : ""}`

  const columnOptions = columnNames.map((name) => ({ value: name, label: name }))

  // Preserve original semantics: the per-row index passed to update/remove is the index within
  // the (possibly sorted) list, applied against item.values by position.
  const valueRows = sortedValues.map((value, vi) => ({ value, vi }))

  const valueColumns: TableProps.ColumnDefinition<{ value: CodelistValue; vi: number }>[] = [
    {
      id: "code",
      header: "Code",
      sortingField: "code",
      cell: (row) =>
        canEdit ? (
          <Input
            value={row.value.code}
            onChange={({ detail }) => updateValue(row.vi, "code", detail.value)}
            invalid={!row.value.code.trim() && showErrors}
            ariaLabel="Code"
          />
        ) : (
          <ReadOnlyValue value={row.value.code} />
        ),
    },
    {
      id: "decode",
      header: "Decode",
      sortingField: "decode",
      cell: (row) =>
        canEdit ? (
          <Input
            value={row.value.decode || ""}
            onChange={({ detail }) => updateValue(row.vi, "decode", detail.value)}
            ariaLabel="Decode"
          />
        ) : (
          <ReadOnlyValue value={row.value.decode} />
        ),
    },
    {
      id: "other_value",
      header: "Other Value",
      sortingField: "other_value",
      cell: (row) =>
        canEdit ? (
          <Input
            value={row.value.other_value || ""}
            onChange={({ detail }) => updateValue(row.vi, "other_value", detail.value)}
            ariaLabel="Other Value"
          />
        ) : (
          <ReadOnlyValue value={row.value.other_value} />
        ),
    },
  ]

  if (canEdit) {
    valueColumns.push({
      id: "actions",
      header: "",
      width: 60,
      cell: (row) => (
        <Button
          variant="icon"
          iconName="close"
          ariaLabel="Remove"
          onClick={() => removeValue(row.vi)}
        />
      ),
    })
  }

  const conditions = item.conditions || []

  const conditionColumns: TableProps.ColumnDefinition<{
    condition: CodelistCondition
    ci: number
  }>[] = [
    {
      id: "column",
      header: "Column",
      cell: (row) =>
        !canEdit ? (
          <ReadOnlyValue value={row.condition.column} />
        ) : columnNames.length > 0 ? (
          <Select
            selectedOption={
              row.condition.column
                ? { value: row.condition.column, label: row.condition.column }
                : null
            }
            onChange={({ detail }) =>
              updateCondition(row.ci, "column", detail.selectedOption.value ?? "")
            }
            options={columnOptions}
            placeholder="Select"
            ariaLabel={`Condition ${row.ci + 1} column`}
          />
        ) : (
          <Input
            value={row.condition.column}
            onChange={({ detail }) => updateCondition(row.ci, "column", detail.value)}
            ariaLabel={`Condition ${row.ci + 1} column`}
          />
        ),
    },
    {
      id: "value",
      header: "Value",
      cell: (row) =>
        canEdit ? (
          <Input
            value={row.condition.value}
            onChange={({ detail }) => updateCondition(row.ci, "value", detail.value)}
            ariaLabel={`Condition ${row.ci + 1} value`}
          />
        ) : (
          <ReadOnlyValue value={row.condition.value} />
        ),
    },
  ]

  if (canEdit) {
    conditionColumns.push({
      id: "actions",
      header: "",
      width: 60,
      cell: (row) => (
        <Button
          variant="icon"
          iconName="close"
          ariaLabel="Remove"
          onClick={() => removeCondition(row.ci)}
        />
      ),
    })
  }

  const valuesError = !item.values.some((v) => v.code.trim()) && showErrors

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText={
        <span>
          {headerLabel}
          {item.source === "sdtm-library" && (
            <>
              {" "}
              <Badge color="blue">SDTM</Badge>
            </>
          )}
          {hasErrors && showErrors && (
            <Box variant="span" color="text-status-error" fontSize="body-s">
              {" "}
              (incomplete)
            </Box>
          )}
        </span>
      }
    >
      <SpaceBetween size="l">
        <SpaceBetween size="l" direction="horizontal">
          <FormField
            label={showConditions ? "Test Concept Name" : "Codelist Name"}
            description={
              duplicateName && showErrors ? undefined : "A name to identify this codelist"
            }
            errorText={
              duplicateName && showErrors
                ? "Duplicate name - each codelist must have a unique name"
                : undefined
            }
          >
            {canEdit ? (
              <Input
                value={item.name}
                onChange={({ detail }) => updateField("name", detail.value)}
                disabled={usedByColumns.length > 0}
                placeholder="e.g., Allowed Sex Values"
                invalid={(!item.name.trim() || duplicateName) && showErrors}
              />
            ) : (
              <ReadOnlyValue value={item.name} />
            )}
          </FormField>

          {showColumn && (
            <FormField
              label="Column"
              description="The column in the vendor data file to validate against"
            >
              {!canEdit ? (
                <ReadOnlyValue value={item.column} />
              ) : columnNames.length > 0 ? (
                <Select
                  selectedOption={item.column ? { value: item.column, label: item.column } : null}
                  onChange={({ detail }) =>
                    updateField("column", detail.selectedOption.value ?? "")
                  }
                  options={columnOptions}
                  invalid={!item.column.trim() && showErrors}
                  placeholder="Select a column"
                />
              ) : (
                <Input
                  value={item.column}
                  onChange={({ detail }) => updateField("column", detail.value)}
                  placeholder="e.g., VISIT"
                  invalid={!item.column.trim() && showErrors}
                />
              )}
            </FormField>
          )}
        </SpaceBetween>

        {showConditions && (
          <div>
            <Box margin={{ bottom: "xs" }}>
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Box variant="awsui-key-label">Conditions ({conditions.length})</Box>
                <Button onClick={addCondition} disabled={!canEdit}>
                  + Add Condition
                </Button>
              </SpaceBetween>
            </Box>
            <Box variant="small" color="text-body-secondary">
              Only validate rows where all conditions match
            </Box>
            {conditions.length > 0 && (
              <Table
                variant="embedded"
                columnDefinitions={conditionColumns}
                items={conditions.map((condition, ci) => ({ condition, ci }))}
                trackBy={(row) => String(row.ci)}
              />
            )}
          </div>
        )}

        <div>
          <Box margin={{ bottom: "xs" }}>
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Box variant="awsui-key-label">Values ({item.values.length})</Box>
              {valuesError && (
                <Box variant="span" color="text-status-error" fontSize="body-s">
                  At least one value with a non-empty code required
                </Box>
              )}
              <Button onClick={addValue} disabled={!canEdit || item.values.length >= 500}>
                + Add Value
              </Button>
            </SpaceBetween>
          </Box>
          {item.values.length > 0 && (
            <Table
              variant="embedded"
              columnDefinitions={valueColumns}
              items={valueRows}
              trackBy={(row) => String(row.vi)}
              sortingColumn={sortCol ? { sortingField: sortCol } : undefined}
              sortingDescending={!sortAsc}
              onSortingChange={({ detail }) =>
                handleSort(detail.sortingColumn.sortingField as keyof CodelistValue)
              }
            />
          )}
        </div>

        {dqdlPreview && (
          <FormField
            label="DQDL Rule Preview"
            description={`This rule will be applied during data ingestion to validate the ${item.column || "specified"} column`}
          >
            <Box variant="code">{dqdlPreview}</Box>
          </FormField>
        )}

        <Box>
          <Button
            variant="normal"
            onClick={() => onRemove(index)}
            disabled={!canEdit || usedByColumns.length > 0}
          >
            Remove
          </Button>
        </Box>
      </SpaceBetween>
    </ExpandableSection>
  )
}
